import { motion, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';

import './App.scss';
import { InteractionConfig } from './types';

const transition = {
	type: 'spring',
	stiffness: 200,
	mass: 0.2,
	damping: 20,
	delay: 0.5,
};

const variants = {
	initial: {
		opacity: 0,
		y: 20,
	},
	enter: {
		opacity: 1,
		y: 0,
		transition,
	},
	exit: {
		opacity: 0,
		y: 20,
		transition: {
			...transition,
			delay: 0,
		},
	},
};

/** Pause after `ended` before hiding the card (then `ended` is posted 300ms after hide, same as images). */
const VIDEO_END_BUFFER_MS = 2500;

/** Start the video anyway if the card's enter animation never reports completion. */
const CARD_ENTER_FALLBACK_MS = 2000;

/** If playback never reaches `ended`, stop waiting after this (broken asset / unsupported codec). */
const VIDEO_FALLBACK_MAX_MS = 10 * 60 * 1000;

const VIDEO_PATH_EXT = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i;

function isVideoUrl(url: string): boolean {
	try {
		const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
		return VIDEO_PATH_EXT.test(u.pathname);
	} catch {
		return VIDEO_PATH_EXT.test(url.split(/[?#]/)[0] ?? url);
	}
}

/** An explicit `picVideoSrc` (e.g. the content policy denial clip) is shown instead of the image. */
function mediaOf(config: InteractionConfig): { src: string; isVideo: boolean } {
	if (config.picVideoSrc) return { src: config.picVideoSrc, isVideo: true };
	return { src: config.picSrc, isVideo: isVideoUrl(config.picSrc) };
}

let askedForConfig = false;

const imageRotation = Math.sign(Math.random() - 0.5) * (1 + Math.random() * 6);

export default function App() {
	const [loaded, setLoaded] = useState(false);
	const [showMessage, setShowMessage] = useState(false);
	// the card has faded in, so a video can start
	const [entered, setEntered] = useState(false);
	const [config, setConfig] = useState<InteractionConfig | null>(null);
	const soundRef = useRef<HTMLAudioElement>(null);
	const narrationRef = useRef<HTMLAudioElement>(null);
	const volumeRef = useRef<number | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const playTimeoutRef = useRef<number>();
	const videoTeardownTimeoutRef = useRef<number>();

	const clearPlayTimeout = () => {
		if (playTimeoutRef.current !== undefined) {
			window.clearTimeout(playTimeoutRef.current);
			playTimeoutRef.current = undefined;
		}
	};

	const clearVideoTeardownTimers = () => {
		if (videoTeardownTimeoutRef.current !== undefined) {
			window.clearTimeout(videoTeardownTimeoutRef.current);
			videoTeardownTimeoutRef.current = undefined;
		}
	};

	const finishInteraction = useCallback(() => {
		clearPlayTimeout();
		setShowMessage(false);
		playTimeoutRef.current = window.setTimeout(() => {
			window.parent?.postMessage({ status: 'ended' }, '*');
			playTimeoutRef.current = undefined;
		}, 300);
	}, []);

	/** Plays the TTS narration (if any) and resolves once it has ended or failed. */
	const playNarration = (variant?: string) =>
		new Promise<void>((resolve) => {
			const el = narrationRef.current;
			if (!el) return resolve();
			el.volume = Math.min(volumeRef.current! * (variant === 'lego' ? 0.4 : 1.3), 1);
			el.addEventListener('ended', () => resolve(), { once: true });
			el.addEventListener('error', () => resolve(), { once: true });
			el.play().catch(() => resolve());
		});

	useEffect(() => {
		if (loaded) window.parent?.postMessage({ status: 'readyToPlay' }, '*');
	}, [loaded]);

	useEffect(() => {
		if (!showMessage || entered || !config?.picSrc || !mediaOf(config).isVideo) return;
		const id = window.setTimeout(() => setEntered(true), CARD_ENTER_FALLBACK_MS);
		return () => window.clearTimeout(id);
	}, [showMessage, entered, config]);

	useEffect(() => {
		if (!showMessage || !entered || !config?.picSrc || !mediaOf(config).isVideo) return;

		const el = videoRef.current;
		if (!el) return;
		el.volume = Math.min(volumeRef.current ?? 0.7, 1);

		let finished = false;
		const done = () => {
			if (finished) return;
			finished = true;
			clearVideoTeardownTimers();
			videoTeardownTimeoutRef.current = window.setTimeout(() => {
				videoTeardownTimeoutRef.current = undefined;
				finishInteraction();
			}, VIDEO_END_BUFFER_MS);
		};

		// the video has its own audio, so the narration only starts once it's over
		const onEnded = () => playNarration(config.Variant).then(done);

		el.addEventListener('ended', onEnded);

		const tryPlay = async () => {
			try {
				await el.play();
			} catch {
				el.muted = true;
				try {
					await el.play();
				} catch {
					window.parent.postMessage({ status: 'error', error: 'failed to play video' }, '*');
				}
			}
		};
		void tryPlay();

		const fallbackId = window.setTimeout(done, VIDEO_FALLBACK_MAX_MS);

		return () => {
			finished = true;
			el.removeEventListener('ended', onEnded);
			window.clearTimeout(fallbackId);
			clearVideoTeardownTimers();
			// don't clear playTimeoutRef: hiding the card re-runs this cleanup, and that timer posts `ended`
		};
	}, [showMessage, entered, config, finishInteraction]);

	useEffect(() => {
		const onmessage = (e: any) => {
			console.log('got message from parent', e.data.cmd);
			try {
				if (e.data.cmd === 'config') {
					console.log('setting config props', e.data.config);
					const configProps = e.data.config as InteractionConfig;
					if (!configProps?.picSrc) {
						throw new Error('no src provided');
					}
					if (!configProps.timeout) {
						configProps.timeout = Number(new URLSearchParams(window.location.search).get('timeout') || '20000');
					}
					setConfig(configProps);
					volumeRef.current = e.data.volume ?? 0.7;
					if (soundRef.current) soundRef.current.volume = volumeRef.current! * 0.6;
					return;
				}

				if (e.data.cmd === 'play') {
					if (!config) {
						throw new Error('no config when playing');
					}

					clearPlayTimeout();
					clearVideoTeardownTimers();

					soundRef.current?.play();
					setEntered(false);
					setShowMessage(true);
					window.parent?.postMessage({ status: 'playing' }, '*');

					// videos drive their own narration and teardown (see the effect above)
					if (!mediaOf(config).isVideo) {
						const onCameraEnded = () => setTimeout(() => void playNarration(config.Variant), 500);
						soundRef.current?.addEventListener('ended', onCameraEnded, { once: true });
						playTimeoutRef.current = window.setTimeout(finishInteraction, config.timeout);
					}
				}
			} catch (error) {
				console.error('error handling message, posting to iframe');
				window.parent.postMessage({ status: 'error', error: error }, '*');
			}
		};
		window.addEventListener('message', onmessage);
		return () => {
			window.removeEventListener('message', onmessage);
			clearPlayTimeout();
			clearVideoTeardownTimers();
		};
	}, [config, finishInteraction]);

	// Tell the window that we are ready to config. This var state is persisted for the lifetime of the site.
	if (!askedForConfig) {
		askedForConfig = true;
		window.parent.postMessage({ status: 'readyToConfig' }, '*');
	}

	const media = config?.picSrc ? mediaOf(config) : null;
	const picIsVideo = !!media?.isVideo;

	return (
		<div className="absolute-fill">
			<audio src={'/camera.mp3'} autoPlay={false} style={{ display: 'none' }} ref={soundRef} />
			{config?.TTSNarrationURL && <audio src={config.TTSNarrationURL} autoPlay={false} style={{ display: 'none' }} ref={narrationRef} />}
			<ol className={'center'}>
				<AnimatePresence>
					{config && showMessage && (
						<motion.div
							key="videodiv"
							initial="initial"
							animate="enter"
							variants={variants}
							layout
							style={{
								display: 'flex',
								alignSelf: 'flex-end',
							}}
							exit="exit"
							onAnimationComplete={(definition) => {
								if (definition === 'enter') setEntered(true);
							}}
						>
							<div className={`${config.custom ? 'custom-' : ''}image-container`} style={{ transform: `rotate(${imageRotation}deg)` }}>
								{picIsVideo ? (
									<video
										ref={videoRef}
										className="the-video"
										src={media!.src}
										playsInline
										preload="metadata"
										controls={false}
										aria-label={config.prompt}
									/>
								) : (
									<img className="the-image" src={config.picSrc} alt={config.prompt}></img>
								)}
								<p className="prompt">{config.prompt}</p>
								<p className="author-name">
									<span className="author-by">By:&nbsp;</span>
									{config.BuyerInfo?.['has-plus'] ? (
										<tangia-plus-name-badge {...config.BuyerInfo}>{config.msgFrom}</tangia-plus-name-badge>
									) : (
										config.msgFrom
									)}
								</p>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</ol>

			{config?.picSrc &&
				(picIsVideo ? (
					<video
						src={media!.src}
						muted
						playsInline
						preload="auto"
						style={{
							width: 1,
							height: 1,
							opacity: 0,
							position: 'absolute',
							pointerEvents: 'none',
						}}
						onLoadedData={() => setLoaded(true)}
						onError={() => window.parent.postMessage({ status: 'error', error: 'failed to load video' }, '*')}
					/>
				) : (
					<img
						src={config.picSrc}
						alt="a dummy"
						style={{
							width: 1,
							height: 1,
							opacity: 0,
						}}
						onLoad={() => setLoaded(true)}
						onError={() => window.parent.postMessage({ status: 'error', error: 'failed to load image' }, '*')}
					/>
				))}
		</div>
	);
}
